import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Sun, CalendarRange, NotebookPen, Settings2, Hash, Plus } from 'lucide-react'
import { useUIStore } from '../store/ui'
import { useTodos, useSections, useCreateTodo } from '../api/hooks'
import { todayStr } from '../lib/parse'
import { todoId } from '../lib/ulid'

interface Command {
  id: string
  label: string
  description?: string
  action: () => void
  icon?: React.ReactNode
}

export function CommandPalette() {
  const open = useUIStore((s) => s.commandPaletteOpen)
  const close = useUIStore((s) => s.closeCommandPalette)
  const setFocusedDate = useUIStore((s) => s.setFocusedDate)
  const navigate = useNavigate()

  const [query, setQuery] = useState('')
  const [selectedIdx, setSelectedIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const { data: todos = [] } = useTodos({})
  const { data: sections = [] } = useSections()
  const createTodo = useCreateTodo()

  // Reset state when palette opens/closes
  useEffect(() => {
    if (open) {
      setQuery('')
      setSelectedIdx(0)
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open])

  const staticCommands: Command[] = useMemo(() => [
    {
      id: 'go-today',
      label: 'Go to Today',
      icon: <Sun size={14} strokeWidth={1.5} />,
      action: () => {
        setFocusedDate(todayStr())
        navigate('/week')
        close()
      },
    },
    {
      id: 'go-week',
      label: 'Week View',
      icon: <CalendarRange size={14} strokeWidth={1.5} />,
      action: () => {
        navigate('/week')
        close()
      },
    },
    {
      id: 'go-notes',
      label: 'Notes',
      icon: <NotebookPen size={14} strokeWidth={1.5} />,
      action: () => {
        navigate('/notes')
        close()
      },
    },
    {
      id: 'go-settings',
      label: 'Settings',
      icon: <Settings2 size={14} strokeWidth={1.5} />,
      action: () => {
        navigate('/settings')
        close()
      },
    },
    ...sections.map((section) => ({
      id: `section-${section.id}`,
      label: section.name,
      description: 'Section',
      icon: <Hash size={14} strokeWidth={1.5} />,
      action: () => {
        navigate(`/sections/${section.id}`)
        close()
      },
    })),
  ], [sections, navigate, close, setFocusedDate])

  // Dynamic "create todo" command when query looks like a task
  const createCommand: Command | null = useMemo(() => {
    if (query.trim().length < 2) return null
    return {
      id: 'create-todo',
      label: `Create: "${query.trim()}"`,
      icon: <Plus size={14} strokeWidth={1.5} />,
      action: () => {
        const maxPos = todos.length > 0 ? Math.max(...todos.map((t) => t.position)) : 0
        createTodo.mutate({
          id: todoId(),
          text: query.trim(),
          status: 'todo',
          date: todayStr(),
          position: maxPos + 1,
        })
        close()
      },
    }
  }, [query, todos, createTodo, close])

  const filteredCommands = useMemo(() => {
    const q = query.toLowerCase()
    const commands = createCommand ? [createCommand, ...staticCommands] : staticCommands
    if (!q) return commands.slice(0, 8)

    return commands
      .filter(
        (cmd) =>
          cmd.label.toLowerCase().includes(q) ||
          cmd.description?.toLowerCase().includes(q)
      )
      .slice(0, 8)
  }, [query, staticCommands, createCommand])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIdx((i) => Math.min(i + 1, filteredCommands.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIdx((i) => Math.max(i - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        filteredCommands[selectedIdx]?.action()
      } else if (e.key === 'Escape') {
        close()
      }
    },
    [filteredCommands, selectedIdx, close]
  )

  // Scroll selected item into view
  useEffect(() => {
    const item = listRef.current?.children[selectedIdx] as HTMLElement | undefined
    item?.scrollIntoView({ block: 'nearest' })
  }, [selectedIdx])

  if (!open) return null

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={close}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(20, 17, 12, 0.45)',
          zIndex: 1000,
          backdropFilter: 'blur(4px) saturate(120%)',
        }}
      />

      {/* Palette */}
      <div
        style={{
          position: 'fixed',
          top: '20%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '540px',
          maxWidth: 'calc(100vw - 32px)',
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '4px',
          boxShadow: 'var(--shadow-card)',
          zIndex: 1001,
          overflow: 'hidden',
        }}
      >
        {/* Search input */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 16px',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <Search size={14} strokeWidth={1.5} style={{ color: 'var(--color-ink-muted)' }} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setSelectedIdx(0)
            }}
            onKeyDown={handleKeyDown}
            placeholder="Search or type a command..."
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              fontFamily: 'var(--font-serif)',
              fontSize: '1rem',
              color: 'var(--color-ink)',
            }}
          />
          <kbd
            style={{
              fontSize: '0.75rem',
              color: 'var(--color-ink-faint)',
              fontFamily: 'var(--font-mono)',
              padding: '2px 6px',
              border: '1px solid var(--color-border)',
              borderRadius: '3px',
            }}
          >
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} style={{ maxHeight: '320px', overflowY: 'auto' }}>
          {filteredCommands.length === 0 ? (
            <div
              style={{
                padding: '16px',
                color: 'var(--color-ink-muted)',
                fontFamily: 'var(--font-serif)',
                fontSize: '0.875rem',
                textAlign: 'center',
              }}
            >
              No results
            </div>
          ) : (
            filteredCommands.map((cmd, idx) => (
              <div
                key={cmd.id}
                onClick={cmd.action}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 16px',
                  cursor: 'pointer',
                  background: idx === selectedIdx ? 'var(--color-paper-dark)' : 'transparent',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={() => setSelectedIdx(idx)}
              >
                <span
                  style={{
                    width: '20px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-ink-muted)',
                    flexShrink: 0,
                  }}
                >
                  {cmd.icon}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontFamily: 'var(--font-serif)',
                      fontSize: '0.9375rem',
                      color: 'var(--color-ink)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {cmd.label}
                  </div>
                  {cmd.description && (
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: 'var(--color-ink-muted)',
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      {cmd.description}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div
          style={{
            padding: '6px 16px',
            borderTop: '1px solid var(--color-border)',
            display: 'flex',
            gap: '16px',
            background: 'var(--color-paper-dark)',
          }}
        >
          {[
            ['↑↓', 'navigate'],
            ['↵', 'select'],
            ['esc', 'close'],
          ].map(([key, label]) => (
            <span key={key} style={{ fontSize: '0.6875rem', color: 'var(--color-ink-faint)', fontFamily: 'var(--font-mono)' }}>
              <kbd style={{ border: '1px solid var(--color-border)', padding: '2px 6px', borderRadius: '3px' }}>{key}</kbd> {label}
            </span>
          ))}
        </div>
      </div>
    </>
  )
}
